import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type {
  ApiClient, EchteBelege, Layout, LayoutGrafik, LayoutVorschauAnfrage, Nummerierung,
} from "@kundeninfo/api-client";
import { Arbeitsdialog, Modal, ModalFuss, darfAbbrechen, useBestaetigung } from "@kundeninfo/ui";
import { Auswahl, Chip, Hinweis, Knopf, Optionen, Schalter, Steckbrief } from "@kundeninfo/ui/me-design";
import { LayoutBlatt } from "./LayoutBlatt.js";
import type { BlattLage, BlattObjekt } from "./LayoutBlatt.js";
import { LayoutRegisterPanel, LayoutReiterleiste, panelId } from "./LayoutRegister.js";
import type { RegisterEintrag, RegisterId, ReiterleisteElement } from "./LayoutRegister.js";
import { LayKnopf, PanelSperre, feldId } from "./register/felder.js";
import { SeitePanel } from "./register/Seite.js";
import { SektionenPanel } from "./register/Sektionen.js";
import { TabellePanel } from "./register/Tabelle.js";
import { BloeckePanel } from "./register/Bloecke.js";
import { TextePanel } from "./register/Texte.js";
import { MarkePanel, STEMPEL_VORGABE, UNTERSCHRIFT_VORGABE } from "./register/Marke.js";
import type { GrafikTeil, GrafikWege, GrafikZug, LogoWege } from "./register/Marke.js";
import { useLayoutVorschau } from "./useLayoutVorschau.js";
import { LAYOUT_TEXT } from "./layoutTexts.js";
import { LayoutVarianten, verwendungText } from "./LayoutVarianten.js";
import type { LayoutVariantenClient } from "./LayoutVarianten.js";
import {
  aufZehntel, fehlerJeReiter, feldFehlerKarte, grafikAufSeite, fussLinien, grafikNameAus, istGleich, kopfbereichMm,
  logoBegrenzt, logoHoeheMm, logoKanteMm, logoLage, logoProzent, mitLogoLage, mitWert,
  ohneGeaenderte, katalogMitFaehigkeiten,
  reiterFuer, spaltenAus, spaltenLinien, verschoben, wertAn, zahlAus,
} from "./layoutEntwurf.js";
import type { Entwurf, LogoKontext } from "./layoutEntwurf.js";
import "./Layout.css";

/**
 * DER LAYOUT-EDITOR (Spec „Layout-Editor, Etappe 1", Oberfläche) — seit dem Paket
 * „Editor-Arbeitsdialog“ (Aufgabe 2; Entscheide 27, 53 a, 55) ein vollflächiger
 * ARBEITSDIALOG über der Schale (`Arbeitsdialog` aus @kundeninfo/ui, Titel = Bereichsname
 * „Belegdesign“). Kopf, Meldungen, Reiter und Fussleiste sind Slots des Elements:
 *
 *  - `steckbrief`: `Steckbrief art="dialog"` mit Layoutname/Unternehmen als Titel, der
 *    Ablage-Kennung, dem Status-Chip und den Kennwerten (Falt-Knopf vom Element);
 *  - `meldungen`: Fremder Stand, Störung, Quittung, Ablage-Hinweis — oben, vor allem
 *    anderen (Hausregel), Schlechtestes oben;
 *  - die Reiterleiste (Fehlerzähler je Reiter) steht im Körper direkt über ihrem Panel —
 *    der Slot `register` bleibt leer (Review A2 W1, me-design Befund 30);
 *  - Fuss: links „Schließen“ und „Verwerfen“, Mitte der Zustandstext, rechts „Speichern“ und „Freigeben“.
 *
 * Der KÖRPER (Werkzeugleiste, Blatt, Gestaltungspanel als Grid) bleibt derselbe Baum über
 * Reiter- und Belegwechsel — kein Remount, der Scrollstand bleibt. Kreuz und Escape (Entscheid 36: jeder Dialog hat das Kreuz) gehen über `onAbbrechen`: bei ungespeicherten Eingaben die Verwerfen-Rückfrage, dann
 * `onSchliessen` (zur Layoutliste). Subdialoge sind `Modal` im Baum des Arbeitsdialogs und
 * liegen damit gestapelt darüber.
 *
 * Der ENTWURF lebt hier lokal (`entwurf`) neben dem Serverstand (`stand`); „ungespeichert"
 * ist der Unterschied der beiden. Grafik-Wege (K10) antworten mit einem neuen Stand —
 * übernommen wird dann nur Version und Grafiken, der lokale Entwurf bleibt.
 *
 * Farben ausschliesslich über ME-Tokens (Körper) bzw. vom Element, Block `lay__`, keine
 * Domänen-Importe. Felder und Knöpfe sind seit ME-Etappe 4 (P3) ME-Elemente (register/felder.tsx).
 */

export type LayoutEditorClient = Pick<ApiClient, "getLayout" | "saveLayoutDraft" |
  "previewLayout" | "listLayoutRealDocuments" | "publishLayout" | "discardLayout" |
  "adoptLayoutStore" | "createLayoutGraphic" | "updateLayoutGraphic" | "deleteLayoutGraphic" |
  "reorderLayoutGraphics" | "layoutGraphicUrl" | "layoutGraphicImageUrl" |
  "getLayoutCapabilities" | "listNumberingSchemes" | "setLayoutNumbering"> & LayoutVariantenClient;

export type LayoutEditorProps = {
  client: LayoutEditorClient;
  layoutId: string;
  onUnternehmen: (companyId: string) => void;
  onZustand: (z: { ungespeichert: boolean; listeNeu: boolean }) => void;
  /** Der Arbeitsdialog geht zu (nach der Verwerfen-Rückfrage, Entscheid 55). */
  onSchliessen: () => void;
  /** Layout-Varianten: nach „Kopie anlegen" das neue Layout öffnen (der Bereich wechselt die Adresse). */
  onLayoutOeffnen?: (layoutId: string) => void;
  /** Layout-Varianten: gelöscht — der Bereich quittiert über der Liste (der Editor schließt gleich danach), mit dem
   *  `ablageHinweis` des Servers, wenn der Eintrag der Engine liegen blieb (LV7). */
  onGeloescht?: (name: string, ablageHinweis: string | null) => void;
};

// Die Belegarten und ihre Wortlaute kommen aus `katalog.belegarten` der Antwort — die
// Oberfläche kennt die Domäne nicht (Hausregel).
type Belegart = LayoutVorschauAnfrage["belegart"];

type BelegWahl =
  | { art: "MUSTER" }
  | { art: "ECHT"; quelle: "OFFER" | "SALES_ORDER" | "INVOICE"; id: string; titel: string };

type Meldung = { text: string; art: "ok" | "schlecht" };

/** Die Kennung des Logos im Blatt (Grafiken tragen ihre UUID). */
const LOGO_ID = "logo";

const DATUM = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

function meldungAus(f: unknown, ersatz: string): string {
  return f instanceof ApiError ? f.message : ersatz;
}

function mm(wert: unknown, system: string): number {
  const eigen = Number(zahlAus(wert, "mm"));
  return Number.isFinite(eigen) && zahlAus(wert, "mm") !== "" ? eigen : Number(zahlAus(system, "mm"));
}

export function LayoutEditor(p: LayoutEditorProps): ReactElement {
  const [stand, setStand] = useState<Layout | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [entwurf, setEntwurf] = useState<Entwurf>({});
  // Der JÜNGSTE Entwurf auch zwischen zwei Zeichnungen: Ein Register ruft `onEntwurf(neu)`
  // und gleich danach `onFeldVerlassen()` (Schalter, Auswahl, Pfeile) — ein Auftrag aus
  // dem Abschluss dieser Zeichnung trüge noch den alten Entwurf.
  const entwurfRef = useRef<Entwurf>({});
  function entwurfSetzen(neu: Entwurf): void {
    entwurfRef.current = neu;
    setEntwurf(neu);
  }
  // Feldfehler aus zwei Quellen (Spec: die Whitelist greift beim Speichern UND vor der
  // Vorschau): `speicherFehler` aus einer 422 des Speicherns, `vorschauFehler` aus einer
  // 422 der Vorschau. Beide räumt die Änderung des betroffenen Felds (Fixrunde 1, W2).
  const [speicherFehler, setSpeicherFehler] = useState<Record<string, string>>({});
  const [vorschauFehler, setVorschauFehler] = useState<Record<string, string>>({});
  /** Eine Eingabe im Register: neuer Entwurf, die Befunde geänderter Felder fallen weg. */
  function eingabe(neu: Entwurf): void {
    const alt = entwurfRef.current;
    entwurfSetzen(neu);
    setSpeicherFehler((f) => ohneGeaenderte(f, alt, neu));
    setVorschauFehler((f) => ohneGeaenderte(f, alt, neu));
  }
  // Nach einer 409 (R8): Der Serverstand ist fremd — Speichern, Verwerfen und Freigeben
  // bleiben gesperrt, bis „Neu laden" die Sicht geholt hat (Fixrunde 1, W1).
  const [fremd, setFremd] = useState<{ von: string; am: string } | null>(null);
  const [meldung, setMeldung] = useState<Meldung | null>(null);
  const [belegart, setBelegart] = useState<Belegart>("ANGEBOT");
  const [beleg, setBeleg] = useState<BelegWahl>({ art: "MUSTER" });
  const [dialog, setDialog] = useState<null | "echt" | "freigabe" | "verwerfen" | "neuladen">(null);
  const [echte, setEchte] = useState<EchteBelege["items"] | null>(null);
  const [arbeitet, setArbeitet] = useState(false);
  const [zoom, setZoom] = useState<"breite" | "voll">("breite");
  const [hilfslinien, setHilfslinien] = useState(false);
  const [spaltenlinien, setSpaltenlinien] = useState(false);
  const [register, setRegister] = useState<RegisterId>("seite");
  // Die AUSWAHL im Blatt (ME-Etappe 4, P10): Kennung des gewählten Objekts (Grafik-ID oder
  // „logo“) über alle Seiten; gezeigt nur bei offenem Register Marke (Auswahl ↔ Marke).
  const [auswahl, setAuswahl] = useState<string | null>(null);
  const [grafikFehler, setGrafikFehler] =
    useState<{ gid: string; felder: Record<string, string>; meldung?: string } | null>(null);
  // LAUFSPERRE der Grafik-Wege (Fixrunde 2, H1): Ein Weg, der während eines anderen
  // ausgelöst wird (Blur beim Loslassen eines Zugs, zwei Verlassen in einem Takt), wartet
  // in der Kette und startet mit dem JÜNGSTEN Stand — Version und Felder aus `standRef`,
  // die `uebernimm` synchron führt. So endet keiner mit einer alten Version in einer 409.
  const standRef = useRef<Layout | null>(null);
  const konfliktRef = useRef(false);
  const wegKette = useRef<Promise<void>>(Promise.resolve());
  const offeneWege = useRef(0);
  // Eine Grafik-Lage aus dem Blatt, deren Serverweg noch läuft (K10): Rahmen und Register
  // zeigen sie, bis der neue Stand da ist — das Seitenbild zeigt die Grafik noch am alten Ort.
  const [ziehen, setZiehen] = useState<GrafikZug | null>(null);
  // Die Zwischenlage eines Zugs im Blatt (`me-zieht`): nur für die mitlaufenden Registerfelder,
  // nie im Entwurf (kein Dirty-Zustand, kein Weg zum Server) — Ende mit `me-verschoben`.
  const [zug, setZug] = useState<{ id: string; lage: BlattLage } | null>(null);
  // FÄHIGKEIT der Engine (Belegdesign Etappe 2): einmal je Editor gefragt; bis zur Antwort
  // und bei jedem Fehler „aus“ — die neuen Felder bleiben dann verborgen.
  const [faehig, setFaehig] = useState(false);
  // Unterschrift am Ende des Textes (Spec-Nachtrag 11.10.2026): eigene Fähigkeit der Engine
  // (`unterschriftTextende`) — ohne sie bleibt die Auswahl der Platzierung verborgen.
  const [textendeFaehig, setTextendeFaehig] = useState(false);
  // Fuß je Seitentyp (Belegdesign Fußbereich): eigene Fähigkeit der Engine (`fussSeitentypen`) — ohne sie bleibt
  // die Gruppe „Fußzeile" im Register Seite verborgen (die Engine lehnte `spaltenFolgeseiten` ab).
  const [fussFaehig, setFussFaehig] = useState(false);
  // Überschriften mit Ebene (Migration 070): Fähigkeit `positionsUeberschriften` — ohne sie bleibt die Gruppe
  // „Überschriften und Bezeichnungen" im Register Tabelle verborgen (die Engine kennte die Sektionen nicht).
  const [ueberschriftenFaehig, setUeberschriftenFaehig] = useState(false);
  // Block und Sektion „Ansprechpartner“ (Migration 069): eigene Fähigkeit der Engine (`ansprechpartnerBlock`).
  const [apFaehig, setApFaehig] = useState(false);
  // Nach einer 422 des Speicherns: erst `zumErstenFehler()` der Reiterleiste, dann der
  // Fokus aufs erste Feld des gewählten Reiters (Review-Fokus 3). `fehlerSprung` stösst
  // den Sprung an, `fokusWunsch` löst ihn ein, sobald das Panel gezeichnet ist.
  const reiterleiste = useRef<ReiterleisteElement>(null);
  const [fehlerSprung, setFehlerSprung] = useState(0);
  const [fokusWunsch, setFokusWunsch] = useState<string | null>(null);
  const client = p.client;
  const vorschau = useLayoutVorschau((a) => client.previewLayout(p.layoutId, a));
  // ÜBERSCHRIFTEN-NUMMERIERUNG (Migration 048): die Schemata für die Auswahl im Register „Texte" — einmal je
  // Editor; ein Fehler lässt nur „Keine" stehen (die Wahl im Entwurf zeigt der Stand ohnehin).
  const [nummerierungen, setNummerierungen] = useState<Nummerierung[] | null>(null);
  const { bestaetigen } = useBestaetigung();
  const listNumberingSchemes = client.listNumberingSchemes;
  useEffect(() => {
    let abgebrochen = false;
    listNumberingSchemes()
      .then((a) => { if (!abgebrochen) setNummerierungen(a.items); })
      .catch(() => { if (!abgebrochen) setNummerierungen([]); });
    return () => { abgebrochen = true; };
  }, [listNumberingSchemes]);

  const ungespeichert = stand !== null && !istGleich(entwurf, stand.entwurf);
  const geaendert = stand !== null && (stand.geaendert || ungespeichert);
  const feldFehler = useMemo(() => ({ ...vorschauFehler, ...speicherFehler }),
    [vorschauFehler, speicherFehler]);

  // Eine neue Antwort der Vorschau ersetzt deren Feldfehler; eine erfolgreiche räumt sie.
  const vorschauFehlerStand = vorschau.fehler;
  useEffect(() => {
    setVorschauFehler(feldFehlerKarte(vorschauFehlerStand?.felder));
  }, [vorschauFehlerStand]);

  useEffect(() => {
    let abgebrochen = false;
    client.getLayoutCapabilities().then((f) => {
      if (abgebrochen) return;
      setFaehig(f.belegdesign2 === true);
      setTextendeFaehig(f.unterschriftTextende === true);
      setFussFaehig(f.belegdesign2 === true && f.fussSeitentypen === true);
      setUeberschriftenFaehig(f.positionsUeberschriften === true);
      setApFaehig(f.ansprechpartnerBlock === true);
    }, () => {
      if (!abgebrochen) {
        setFaehig(false); setTextendeFaehig(false); setFussFaehig(false); setUeberschriftenFaehig(false);
        setApFaehig(false);
      }
    });
    return () => { abgebrochen = true; };
    // Einmal je Editor (Brief A7, Schritt 1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let abgebrochen = false;
    client.getLayout(p.layoutId).then((s) => {
      if (abgebrochen) return;
      setStand(s);
      standRef.current = s;
      entwurfSetzen(s.entwurf);
      vorschau.sofort({ version: s.version, entwurf: s.entwurf, belegart: "ANGEBOT",
                        beleg: { art: "MUSTER" } });
    }).catch((f: unknown) => {
      if (!abgebrochen) setLadeFehler(meldungAus(f, "Das Layout konnte nicht geladen werden."));
    });
    return () => { abgebrochen = true; };
    // `vorschau` und `client` sind je Anzeige stabil genug; neu geladen wird nur bei
    // einem anderen Layout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.layoutId]);

  const onZustand = p.onZustand;
  useEffect(() => { onZustand({ ungespeichert, listeNeu: false }); }, [ungespeichert, onZustand]);

  // Review-Fokus 3, erster Schritt: der Reiter. `zumErstenFehler()` wählt den ersten Reiter
  // mit Fehlerzähler (und meldet ihn über `me-reiter-gewaehlt` → `setRegister`); gemerkt
  // wird das erste Feld dieses Reiters.
  useEffect(() => {
    if (fehlerSprung === 0) return;
    const kennung = reiterleiste.current?.zumErstenFehler() ?? null;
    const ziel = Object.keys(speicherFehler).find((pfad) => {
      const reiter = reiterFuer(pfad);
      return reiter !== null && panelId(reiter) === kennung;
    });
    setFokusWunsch(ziel === undefined ? null : feldId(ziel));
    // Nur je Sprung — `speicherFehler` ist im selben Takt gesetzt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fehlerSprung]);
  // Zweiter Schritt: das Feld, sobald sein Panel gezeichnet ist (gleicher Takt wie der
  // Reiterwechsel). Ein Befund ohne eigenes Feld (die ganze Spaltenliste) lässt den Fokus
  // am Reiter.
  useEffect(() => {
    if (fokusWunsch === null) return;
    document.getElementById(fokusWunsch)?.focus();
    setFokusWunsch(null);
  }, [fokusWunsch, register]);

  /**
   * Der eine Weg hinaus (Kreuz, Escape, natives Schliessen, „Schließen“ im Fuss; Entscheid 55/36): Stehen ungespeicherte Eingaben da —
   * auch im Zustand „Fremder Stand“ —, fragt die Verwerfen-Rückfrage gestapelt darüber.
   * „Weiter bearbeiten“ lässt Entwurf und Reiter stehen (Review-Fokus 1; den Fokus gibt
   * die Rückfrage selbst an das Feld zurück — kein Nachbau hier); „Verwerfen“ schliesst zur
   * Layoutliste. Gespeicherte, nicht freigegebene Entwürfe gehen nicht verloren — dafür
   * keine Frage. Kein Ablehnungsweg: `darfAbbrechen` löst nur auf (Review A1, H2).
   */
  async function abbrechen(): Promise<void> {
    const jetzt = standRef.current;
    const offen = jetzt !== null && !istGleich(entwurfRef.current, jetzt.entwurf);
    if (await darfAbbrechen(offen)) p.onSchliessen();
  }

  function auftrag(e: Entwurf = entwurf, art: Belegart = belegart,
                   b: BelegWahl = beleg): LayoutVorschauAnfrage {
    return { version: stand?.version ?? 0, entwurf: e, belegart: art,
             beleg: b.art === "MUSTER" ? { art: "MUSTER" }
               : { art: "ECHT", quelle: b.quelle, id: b.id } };
  }

  function fehlerZeigen(f: unknown, ersatz: string): void {
    if (f instanceof ApiError && f.status === 422 && (f.fields ?? []).length > 0) {
      setSpeicherFehler(feldFehlerKarte(f.fields));
      setMeldung({ text: f.message, art: "schlecht" });
      setFehlerSprung((n) => n + 1);
    } else if (f instanceof ApiError && f.status === 409) {
      // Der 409-Rumpf trägt den Repository-Stand, nicht die Sicht (Katalog, Störung) —
      // deshalb lädt „Neu laden" die Sicht über getLayout.
      setFremd({ von: f.conflict?.changedBy ?? "ein anderer Vorgang",
                 am: f.conflict?.changedAt ?? new Date().toISOString() });
      setMeldung(null);
    } else {
      setMeldung({ text: meldungAus(f, ersatz), art: "schlecht" });
    }
  }

  /**
   * Ein neuer Serverstand. `ersetzen`: `true` ersetzt den lokalen Entwurf (Verwerfen,
   * Übernehmen, Neu laden), `false` lässt ihn stehen (Grafik-Wege, Erneut prüfen). Ein
   * ENTWURF als Wert heisst „ersetzen nur, wenn seit dem Absenden nichts getippt wurde"
   * (Speichern, Freigabe — Fixrunde 1, W3): Sonst bleibt die neuere Eingabe stehen und
   * ist eben ungespeichert.
   */
  function uebernimm(neu: Layout, ersetzen: boolean | Entwurf): void {
    setStand(neu);
    standRef.current = neu;
    konfliktRef.current = false;
    const ja = ersetzen === true
      || (ersetzen !== false && istGleich(entwurfRef.current, ersetzen));
    if (ja) {
      entwurfSetzen(neu.entwurf);
      setSpeicherFehler({});
    }
    setFremd(null);
    p.onZustand({ ungespeichert: !istGleich(entwurfRef.current, neu.entwurf), listeNeu: true });
  }

  async function speichern(): Promise<Layout | null> {
    if (stand === null) return null;
    setArbeitet(true);
    setMeldung(null);
    try {
      const gesendet = entwurfRef.current;
      const neu = await client.saveLayoutDraft(stand.id, stand.version, gesendet);
      uebernimm(neu, gesendet);
      setSpeicherFehler({});
      setMeldung({ text: "Entwurf gespeichert.", art: "ok" });
      return neu;
    } catch (f) {
      fehlerZeigen(f, "Der Entwurf konnte nicht gespeichert werden.");
      return null;
    } finally {
      setArbeitet(false);
    }
  }

  /**
   * Die Nummerierung des Entwurfs wählen (Migration 048): eigener Schreibweg mit Versionsriegel und gestapelter
   * Bestätigung (Hausregel 19.10.) — der getippte Entwurf bleibt stehen (`uebernimm(…, false)`), wirksam wird die
   * Wahl mit der nächsten Freigabe. Danach zeichnet die Vorschau neu.
   */
  async function nummerierungWaehlen(id: string | null): Promise<void> {
    const jetzt = standRef.current;
    if (jetzt === null || fremd !== null || id === jetzt.nummerierung.entwurfId) return;
    const name = id === null ? "Keine" : nummerierungen?.find((n) => n.id === id)?.name ?? "unbekannt";
    if (!(await bestaetigen({ titel: `Nummerierung ${name} wählen?`, bestaetigenText: "Wählen",
      text: "Die Überschriften der Belegtexte tragen sie ab der nächsten Freigabe." }))) return;
    setArbeitet(true);
    try {
      uebernimm(await client.setLayoutNumbering(jetzt.id, jetzt.version, id), false);
      vorschau.anfordern(auftrag(entwurfRef.current));
    } catch (f) {
      fehlerZeigen(f, "Die Nummerierung konnte nicht gewählt werden.");
    } finally {
      setArbeitet(false);
    }
  }

  /** Freigeben öffnet erst nach dem Speichern eines ungespeicherten Entwurfs — auch aus
   *  der Störungszeile („Erneut freigeben"): Freigegeben wird nie ein Stand, der nicht der
   *  sichtbare ist (Notiz K8 → K10). */
  async function freigabeOeffnen(): Promise<void> {
    if (fremd !== null) return;
    if (ungespeichert && (await speichern()) === null) return;
    setDialog("freigabe");
  }

  async function freigeben(): Promise<void> {
    // Riegel auch hier: kein fremder und kein ungespeicherter Stand geht hinaus.
    if (stand === null || fremd !== null || !istGleich(entwurfRef.current, stand.entwurf)) return;
    setArbeitet(true);
    try {
      // Freigegeben wird der GESPEICHERTE Entwurf; was danach getippt wurde, bleibt stehen.
      const gespeichert = stand.entwurf;
      const neu = await client.publishLayout(stand.id, stand.version);
      uebernimm(neu, gespeichert);
      setMeldung({ text: neu.vorgabe
        ? `Freigegeben — ab jetzt rendert jeder Beleg von ${neu.unternehmen.name} ohne eigene `
          + "Layoutwahl mit diesem Layout."
        : `Freigegeben — ab jetzt rendert jeder Beleg mit dem Layout „${neu.name}“ damit.`, art: "ok" });
    } catch (f) {
      fehlerZeigen(f, "Die Freigabe ist fehlgeschlagen — nichts wurde geändert.");
    } finally {
      setArbeitet(false);
      setDialog(null);
    }
  }

  async function verwerfen(): Promise<void> {
    if (stand === null) return;
    setArbeitet(true);
    try {
      const neu = await client.discardLayout(stand.id, stand.version);
      uebernimm(neu, true);
      vorschau.sofort(auftrag(neu.entwurf));
      setMeldung({ text: "Entwurf verworfen.", art: "ok" });
    } catch (f) {
      fehlerZeigen(f, "Der Entwurf konnte nicht verworfen werden.");
    } finally {
      setArbeitet(false);
      setDialog(null);
    }
  }

  /**
   * Ein GRAFIK-WEG: Serverweg mit dem aktuellen Versionsriegel, eingereiht in die
   * Laufsperre (`wegKette`). Übernommen werden nur Version und Grafiken (der lokale Entwurf
   * bleibt, `uebernimm(…, false)`), danach rendert das Blatt sofort. Eine 422 mit Feldern
   * landet an der Grafik (`gid`; Pfade ohne Listenpräfix `grafiken[i].`), die Meldung im
   * Kopf — beim Anlegen (`gid` „neu") stattdessen beides im Panel unter „SVG hochladen"
   * (Fixrunde 2, W1). Eine 409 führt wie bei Speichern und Verwerfen in den Zustand
   * „Fremder Stand" (K8); wartende Wege fallen dann weg.
   */
  function grafikWeg(gid: string, weg: (version: number) => Promise<Layout>): Promise<void> {
    if (standRef.current === null || fremd !== null) return Promise.resolve();
    offeneWege.current += 1;
    setArbeitet(true);
    const lauf = async (): Promise<void> => {
      try {
        await einGrafikWeg(gid, weg);
      } finally {
        offeneWege.current -= 1;
        if (offeneWege.current === 0) setArbeitet(false);
      }
    };
    // Auch nach einer (nicht erwarteten) Ablehnung des Vorgängers läuft der nächste Weg —
    // sonst sänke `offeneWege` nie, und der Editor bliebe gesperrt (Gesamtreview F4).
    const naechster = wegKette.current.then(lauf, lauf);
    wegKette.current = naechster;
    return naechster;
  }

  async function einGrafikWeg(gid: string, weg: (version: number) => Promise<Layout>):
      Promise<void> {
    const jetzt = standRef.current;
    if (jetzt === null || konfliktRef.current) return;
    setGrafikFehler(null);
    setMeldung(null);
    try {
      const neu = await weg(jetzt.version);
      uebernimm(neu, false);
      vorschau.sofort({ ...auftrag(entwurfRef.current), version: neu.version });
    } catch (f) {
      if (f instanceof ApiError && f.status === 409) {
        konfliktRef.current = true;
        setFremd({ von: f.conflict?.changedBy ?? "ein anderer Vorgang",
                   am: f.conflict?.changedAt ?? new Date().toISOString() });
        return;
      }
      const felder = f instanceof ApiError && f.status === 422
        ? feldFehlerKarte((f.fields ?? []).map((b) => ({
            field: b.field.replace(/^(layout\.)?grafiken\[\d+\]\./, ""), message: b.message })))
        : {};
      const text = meldungAus(f, "Die Grafik konnte nicht gespeichert werden.");
      if (gid === "neu") {
        setGrafikFehler({ gid, felder, meldung: text });
        return;
      }
      if (Object.keys(felder).length > 0) setGrafikFehler({ gid, felder });
      setMeldung({ text, art: "schlecht" });
    }
  }

  /** Die Felder einer Grafik für einen Änderungsweg — aus dem JÜNGSTEN Stand, damit ein
   *  wartender Weg die Änderung seines Vorgängers nicht zurückdreht (Fixrunde 2, H1). */
  function felderVon(g: LayoutGrafik, teil: GrafikTeil, version: number) {
    const a = standRef.current?.grafiken.find((x) => x.id === g.id) ?? g;
    return { version, name: a.name, pages: a.pages, xMm: a.xMm, yMm: a.yMm,
             widthMm: a.widthMm, opacity: a.opacity, caption: a.caption,
             documentKinds: [...a.documentKinds], ...teil };
  }

  function grafikAendern(g: LayoutGrafik, teil: GrafikTeil): Promise<void> {
    return grafikWeg(g.id, (v) => client.updateLayoutGraphic(p.layoutId, g.id,
      felderVon(g, teil, v)));
  }

  function begrenzt(g: { widthMm: number; heightMm: number | null }, xMm: number, yMm: number):
      { xMm: number; yMm: number } {
    // R-LAGE: x + Breite ≤ 210, 0 ≤ y ≤ 297 — gezogen wird nie über die Blattkante; mit
    // Kastenhöhe (Etappe 2) endet auch die Unterkante im Blatt (y + Höhe ≤ 297).
    return { xMm: aufZehntel(Math.min(Math.max(xMm, 0), 210 - g.widthMm)),
             yMm: aufZehntel(Math.min(Math.max(yMm, 0), 297 - (g.heightMm ?? 0))) };
  }

  /**
   * Eine neue Lage aus dem Blatt (ME-Etappe 4, P10: `me-verschoben` nach Zug, Griff oder
   * Taste — der Massstab ist Sache des Elements, ein Zug trifft bei „Breite“ und „100 %“
   * dieselben Millimeter). Das Logo ist eine Entwurfslage; eine Grafik geht über ihren
   * Serverweg, bis zur Antwort schwebt die Lage (Rahmen und Register zeigen sie). Was der
   * Editor begrenzt, zieht das Element danach von selbst nach (kontrolliert).
   */
  function blattVerschoben(id: string, l: BlattLage): void {
    setZug(null);
    if (fremd !== null) return;
    if (id === LOGO_ID) { logoVerschoben(l); return; }
    if (arbeitet) return;
    const g = standRef.current?.grafiken.find((x) => x.id === id);
    if (g === undefined) return;
    // Breite 5–200 mm, Höhe 5–297 mm (R-LAGE); ohne Kastenhöhe zählt nur die Breite (der
    // Rahmen ist dann eine Markierung 3 : 1).
    const { gid: _gid, widthMm, heightMm, ...lage } = grafikNach(g, l);
    const teil: GrafikTeil = {};
    if (lage.xMm !== g.xMm) teil.xMm = lage.xMm;
    if (lage.yMm !== g.yMm) teil.yMm = lage.yMm;
    if (widthMm !== g.widthMm) teil.widthMm = widthMm;
    if (heightMm !== null && heightMm !== g.heightMm) teil.heightMm = heightMm;
    if (Object.keys(teil).length === 0) return;
    setZiehen({ gid: g.id, ...lage, widthMm, heightMm });
    void grafikAendern(g, teil).finally(() => setZiehen(null));
  }

  /** Die Bildquelle einer festen Grafik — SVG oder Rasterbild nach `bildTyp`
   *  (Entscheid 25). */
  function grafikBildUrl(gid: string): string {
    if (stand === null) return "";
    const typ = stand.grafiken.find((g) => g.id === gid)?.bildTyp ?? null;
    return typ === "image/png" || typ === "image/jpeg"
      ? client.layoutGraphicImageUrl(stand.id, gid, stand.version)
      : client.layoutGraphicUrl(stand.id, gid, stand.version);
  }

  /** Die Rahmen einer Seite: das Logo und die Grafiken dieser Seite und Belegart. */
  function blattObjekte(seite: number): BlattObjekt[] {
    if (stand === null) return [];
    const logo = logoObjekt(seite);
    // Ein Unterschriftsplatz am Textende hat keine feste Lage zum Ziehen — die Vorschau der
    // Engine zeigt, wo er landet (Hinweis im Register Marke).
    const grafiken = stand.grafiken.filter((g) => grafikAufSeite(g, seite, belegart)
      && !(g.kind === "SIGNATURE" && g.placement === "TEXT_END")).map((g): BlattObjekt => {
      const schwebe = ziehen?.gid === g.id ? ziehen : null;
      const lage = schwebe ?? g;
      // Mit Kastenhöhe (Etappe 2) zeigt der Rahmen den echten Kasten B × H; ohne bleibt er
      // eine Markierung im festen Verhältnis 3 : 1 (R-LAGE), deren Griffe das Verhältnis
      // halten. Der Kasten MIT Höhe ist an den Griffen frei (Entscheid 46): Breite und Höhe
      // einzeln, wie über die Felder im Register Marke (Entscheid 72); die Grafik darin
      // behält ihre Proportionen — die Engine passt sie ein (contain, unten aufgesetzt).
      const mitHoehe = g.heightMm !== null;
      return {
        id: g.id, beschriftung: g.name, xMm: lage.xMm, yMm: lage.yMm, breiteMm: lage.widthMm,
        hoeheMm: mitHoehe ? (lage.heightMm ?? g.heightMm ?? 0) : lage.widthMm / 3,
        verhaeltnisHalten: !mitHoehe, minBreiteMm: 5, minHoeheMm: mitHoehe ? 5 : 5 / 3,
        klasse: `lay__rahmen${mitHoehe ? " lay__rahmen--hoehe" : ""}`,
        // Während des Zugs zeigt das Seitenbild die Grafik noch am alten Ort — der Rahmen
        // trägt sie darum als Schatten mit (SVG wie PNG/JPEG, Entscheid 25).
        schatten: g.kind === "STATIC"
          ? <img className="lay__rahmenbild" src={grafikBildUrl(g.id)} alt="" draggable={false} />
          : undefined,
        schwebend: schwebe !== null,
      };
    });
    return logo === null ? grafiken : [logo, ...grafiken];
  }

  /** Was Lage, Ziehen und Vorschlag des Logos brauchen — aus dem JÜNGSTEN Entwurf. */
  function logoKontext(e: Entwurf): { kante: number; kontext: LogoKontext } {
    const k = stand!.katalog;
    const kante = logoKanteMm(e, k.logo.breiteMm);
    return { kante, kontext: {
      vorschlag: { xMm: mm(wertAn(e, ["seitenraender", "links"]), k.seitenraender.links),
                   yMm: mm(wertAn(e, ["seitenraender", "oben"]), k.seitenraender.oben) },
      hoeheMm: logoHoeheMm(kante, stand!.unternehmen.logoMasse),
      kopfMinMm: Math.max(mm(wertAn(e, ["seitenraender", "oben"]), k.seitenraender.oben),
                          k.kopfbereich.minMm),
      kopfMaxMm: k.kopfbereich.maxMm,
    } };
  }

  /**
   * Eine neue Lage oder Größe des Logos aus dem Blatt (Zug, Griff, Pfeiltaste) in den
   * Entwurf — vom JÜNGSTEN Entwurf aus (zwei Drucke in einem Takt gehen beide). Die Breite
   * wird zu Prozent der Grundbreite (25–200 %, ganze Prozent, Entscheid 65 a/69), die Höhe
   * folgt dem eingepassten Logo; die Lage bleibt im Kopfbereich (Kopplung, Entscheide
   * 66–69), der Pflicht-Kopfbereich wird beim ersten Setzen vorgeschlagen. Die Vorschau
   * folgt entprellt.
   */
  function logoVerschoben(l: BlattLage): void {
    const alt = logoLage(entwurfRef.current);
    const neu = logoNach(entwurfRef.current, l);
    if (neu.entwurf === entwurfRef.current && alt !== null
        && neu.lage.xMm === alt.xMm && neu.lage.yMm === alt.yMm) return;
    eingabe(neu.entwurf);
    vorschau.anfordern(auftrag(entwurfRef.current));
  }

  /** Der Entwurf, den eine Logo-Lage aus dem Blatt ergäbe (rein, schreibt nichts) — für
   *  `logoVerschoben` und für die mitlaufenden Felder während des Zugs. */
  function logoNach(e: Entwurf, l: BlattLage):
      { entwurf: Entwurf; lage: { xMm: number; yMm: number } } {
    const k = stand!.katalog.logo;
    const { kante: alteKante } = logoKontext(e);
    if (Math.abs(l.widthMm - alteKante) >= 0.05) {
      const prozent = Math.min(Math.max(Math.round(l.widthMm / k.breiteMm * 100), 25), 200);
      if (prozent !== logoProzent(e)) e = mitWert(e, ["logo", "prozent"], prozent);
    }
    const { kante, kontext } = logoKontext(e);
    const lage = logoBegrenzt(e, l.xMm, l.yMm, kante, kontext);
    return { entwurf: mitLogoLage(e, lage, kontext), lage };
  }

  /** Die Grafiklage, die `blattVerschoben` aus einer Blattlage macht (begrenzt, auf Zehntel). */
  function grafikNach(g: LayoutGrafik, l: BlattLage): GrafikZug {
    const widthMm = Math.min(Math.max(aufZehntel(l.widthMm), 5), 200);
    const heightMm = g.heightMm === null ? null
      : Math.min(Math.max(aufZehntel(l.heightMm), 5), 297);
    return { gid: g.id, ...begrenzt({ widthMm, heightMm }, l.xMm, l.yMm), widthMm, heightMm };
  }

  /** Die Zwischenlage eines Zugs: die Registerfelder laufen mit, der Entwurf und der
   *  Dirty-Zustand bleiben unberührt (geschrieben wird erst mit `me-verschoben`). */
  function blattZieht(id: string, l: BlattLage | null): void {
    if (l === null || fremd !== null || arbeitet) { setZug(null); return; }
    if (id === LOGO_ID || standRef.current?.grafiken.some((x) => x.id === id) === true) {
      setZug({ id, lage: l });
    }
  }

  /**
   * Der RAHMEN des Logos (Aufgabe 7): echte Box Kante × eingepasste Höhe. Auf Seite 1
   * frei immer, im Textfluss an der Vorschlagslage (der erste Zug setzt die Lage) nur bei
   * offenem Register Marke oder Hilfslinien; auf den Folgeseiten nur frei und ohne „nur
   * Seite 1“. Nur mit Fähigkeit und einem Logo mit lesbaren Maßen.
   */
  function logoObjekt(seite: number): BlattObjekt | null {
    // An den Maßen, nicht an `logoVorhanden`: nur ein Logo, mit dem Belege rendern (Review
    // A7 H2). Im Textfluss nur bei offenem Register Marke oder Hilfslinien — sonst finge der
    // Rahmen an der Vorschlagslage Klicks ins Blatt ab (Review A7, Hinweis b).
    if (stand === null || !faehig || stand.unternehmen.logoMasse === null) return null;
    const frei = logoLage(entwurf);
    if (frei === null && register !== "marke" && !hilfslinien) return null;
    if (seite > 1 && (frei === null || wertAn(entwurf, ["logo", "nurSeite1"]) === true)) {
      return null;
    }
    const { kante, kontext } = logoKontext(entwurf);
    const lage = frei ?? kontext.vorschlag;
    return {
      id: LOGO_ID, beschriftung: frei === null ? "Logo (Textfluss)" : "Logo",
      xMm: lage.xMm, yMm: lage.yMm, breiteMm: kante, hoeheMm: kontext.hoeheMm,
      verhaeltnisHalten: true,
      minBreiteMm: stand.katalog.logo.breiteMm * 0.25,
      minHoeheMm: Math.min(kontext.hoeheMm, 1),
      klasse: "lay__rahmen lay__rahmen--hoehe",
    };
  }

  async function ablageUebernehmen(): Promise<void> {
    if (stand === null) return;
    // Erst die Rückfrage (Entscheid 687) — rot nur, wenn dabei ungespeicherte Eingaben verloren gehen.
    if (!(await bestaetigen({ titel: LAYOUT_TEXT.adoptStoreTitle, text: LAYOUT_TEXT.adoptStoreText(ungespeichert),
      bestaetigenText: LAYOUT_TEXT.adoptStore, gefaehrlich: ungespeichert }))) return;
    setArbeitet(true);
    try {
      const neu = await client.adoptLayoutStore(stand.id, stand.version);
      uebernimm(neu, true);
      vorschau.sofort(auftrag(neu.entwurf));
      setMeldung({ text: "Der Stand der Ablage ist übernommen.", art: "ok" });
    } catch (f) {
      fehlerZeigen(f, "Der Stand der Ablage konnte nicht übernommen werden.");
    } finally {
      setArbeitet(false);
    }
  }

  /** Die Sicht neu holen. `mitEntwurf`: auch den lokalen Entwurf ersetzen (nach 409,
   *  bei ungespeicherten Eingaben erst nach Rückfrage); sonst bleibt er (Erneut prüfen).
   *
   *  Erneut prüfen (K8 N1): Hat inzwischen jemand anders gespeichert (neue Version) und
   *  stehen hier ungespeicherte Eingaben, wird NICHTS übernommen — der Editor geht in den
   *  Zustand „Fremder Stand"; sonst überschriebe das nächste Speichern die fremde Änderung
   *  ohne 409. Ohne eigene Eingaben ersetzt der neue Stand den Entwurf (nichts geht
   *  verloren, und der alte Entwurf gälte sonst als ungespeichert). */
  async function neuLaden(mitEntwurf: boolean): Promise<void> {
    const alt = stand;
    setArbeitet(true);
    try {
      const neu = await client.getLayout(p.layoutId);
      const eigeneEingaben = alt !== null && !istGleich(entwurfRef.current, alt.entwurf);
      if (!mitEntwurf && alt !== null && neu.version !== alt.version && eigeneEingaben) {
        setFremd({ von: "ein anderer Vorgang", am: new Date().toISOString() });
        setMeldung(null);
        return;
      }
      const ersetzen = mitEntwurf || !eigeneEingaben;
      uebernimm(neu, ersetzen);
      setMeldung(null);
      vorschau.sofort({ ...auftrag(ersetzen ? neu.entwurf : entwurfRef.current),
                        version: neu.version });
    } catch (f) {
      setMeldung({ text: meldungAus(f, "Das Layout konnte nicht geladen werden."),
                   art: "schlecht" });
    } finally {
      setArbeitet(false);
      setDialog(null);
    }
  }

  async function echteLaden(): Promise<void> {
    setDialog("echt");
    setEchte(null);
    try {
      setEchte((await client.listLayoutRealDocuments(p.layoutId)).items);
    } catch (f) {
      setEchte([]);
      setMeldung({ text: meldungAus(f, "Die Belege konnten nicht geladen werden."),
                   art: "schlecht" });
    }
  }

  function belegWaehlen(b: BelegWahl): void {
    setBeleg(b);
    setDialog(null);
    vorschau.sofort(auftrag(entwurf, belegart, b));
  }


  if (stand === null) {
    // Derselbe Arbeitsdialog wie danach (gleicher Baum): Er öffnet sofort und bleibt offen,
    // wenn der Stand eintrifft. Ohne Stand gibt es nichts zu verwerfen.
    return (
      <Arbeitsdialog titel="Belegdesign" onAbbrechen={() => p.onSchliessen()}
        fussLinks={<Knopf art="sekundaer" onClick={() => p.onSchliessen()}>Schließen</Knopf>}
        meldungen={ladeFehler === null ? undefined : (
          <Hinweis art="fehler" className="lay__meldung" aria-label="Ladefehler">{ladeFehler}</Hinweis>
        )}>
        {ladeFehler === null && <p className="lay__hinweis" role="status">Lädt …</p>}
      </Arbeitsdialog>
    );
  }

  const k = stand.katalog;
  // Was die Registerfelder zeigen: die Zwischenlage des Zugs (Logo: der Entwurf, den das
  // Loslassen ergäbe; Grafik: die begrenzte Lage), sonst Entwurf und Serverstand.
  const zugGrafik = zug !== null && zug.id !== LOGO_ID
    ? stand.grafiken.find((g) => g.id === zug.id) : undefined;
  const ziehenAnzeige: GrafikZug | null = ziehen
    ?? (zug !== null && zugGrafik !== undefined ? grafikNach(zugGrafik, zug.lage) : null);
  const entwurfAnzeige = zug !== null && zug.id === LOGO_ID
    ? logoNach(entwurf, zug.lage).entwurf : entwurf;
  const wege: GrafikWege = {
    grafikUrl: (gid) => grafikBildUrl(gid),
    arbeitet: arbeitet || fremd !== null, faehig, textendeFaehig, fehler: grafikFehler, ziehen: ziehenAnzeige,
    onHochladen: (datei) => {
      void grafikWeg("neu", (v) => client.createLayoutGraphic(stand.id,
        { version: v, kind: "STATIC", name: grafikNameAus(datei.name), ...STEMPEL_VORGABE },
        datei));
    },
    onUnterschriftsplatz: () => {
      // Neue Plätze starten mit Höhe = Breite / 3 (Entscheid 72) — nur, wenn die Engine
      // die Kastenhöhe kennt. Die Platzierung (am Textende, wenn die Engine es kann,
      // Entscheid 84) wählt der Server.
      void grafikWeg("neu", (v) => client.createLayoutGraphic(stand.id,
        { version: v, kind: "SIGNATURE", ...UNTERSCHRIFT_VORGABE,
          ...(faehig ? { heightMm: aufZehntel(UNTERSCHRIFT_VORGABE.widthMm / 3) } : {}) },
        null));
    },
    onAendern: (g, teil) => { void grafikAendern(g, teil); },
    onEntfernen: (g) => {
      void grafikWeg(g.id, (v) => client.deleteLayoutGraphic(stand.id, g.id, v));
    },
    onVerschieben: (von, nach) => {
      // Die Reihenfolge erst IM Weg aus dem jüngsten Stand (Gesamtreview F5, wie felderVon).
      void grafikWeg("reihenfolge", (v) => client.reorderLayoutGraphics(stand.id, v,
        verschoben((standRef.current ?? stand).grafiken.map((g) => g.id), von, nach)));
    },
    onUnternehmen: () => p.onUnternehmen(stand.companyId),
  };
  // Das Startregister bleibt „Seite" (K8); beim Ziehen springt der Editor auf „Marke",
  // damit die Lage gleichzeitig im Register steht (Spec).
  const logoKennwerte = logoKontext(entwurfAnzeige);
  const logoWege: LogoWege = {
    faehig, masse: stand.unternehmen.logoMasse, grundbreiteMm: k.logo.breiteMm,
    kontext: logoKennwerte.kontext, gesperrt: fremd !== null,
  };
  const eintraege: RegisterEintrag[] = [
    { id: "marke", label: "Marke",
      panel: (pp) => <MarkePanel {...pp} wege={wege} logo={logoWege} /> },
    { id: "seite", label: "Seite",
      panel: (pp) => <SeitePanel {...pp} faehig={faehig} fussFaehig={fussFaehig}
                                 onUnternehmen={() => p.onUnternehmen(stand.companyId)} /> },
    { id: "sektionen", label: "Sektionen", panel: (pp) => <SektionenPanel {...pp} /> },
    { id: "tabelle", label: "Tabelle",
      panel: (pp) => <TabellePanel {...pp} faehig={faehig} /> },

    { id: "bloecke", label: "Blöcke", panel: (pp) => <BloeckePanel {...pp} faehig={faehig} /> },
    { id: "texte", label: "Texte", panel: (pp) => <TextePanel {...pp} /> },
  ];
  // Fehlerzähler je Reiter: Feldbefunde über die Zuordnung Feldpfad → Reiter, dazu die
  // Befunde einer Grafik am Reiter „Marke“.
  const fehlerZahl = fehlerJeReiter(feldFehler);
  if (grafikFehler !== null) {
    const n = Object.keys(grafikFehler.felder).length
      || (grafikFehler.meldung === undefined ? 0 : 1);
    if (n > 0) fehlerZahl.marke = (fehlerZahl.marke ?? 0) + n;
  }
  const raender = {
    oben: mm(wertAn(entwurf, ["seitenraender", "oben"]), k.seitenraender.oben),
    rechts: mm(wertAn(entwurf, ["seitenraender", "rechts"]), k.seitenraender.rechts),
    unten: mm(wertAn(entwurf, ["seitenraender", "unten"]), k.seitenraender.unten),
    links: mm(wertAn(entwurf, ["seitenraender", "links"]), k.seitenraender.links),
  };
  const chip = stand.archiviertAm !== null ? { text: "archiviert", art: "aus" as const }
    : stand.stoerung === "ABLAGE_NICHT_ERREICHBAR"
    ? { text: "Ablage nicht erreichbar", art: "warnung" as const }
    : stand.stoerung !== null ? { text: "Störung", art: "fehler" as const }
    : geaendert ? { text: "Entwurf geändert", art: "warnung" as const }
    : stand.freigegeben !== null ? { text: "freigegeben, keine Änderungen", art: "ok" as const }
    : { text: "nicht freigegeben", art: "aus" as const };
  const blattFehler = vorschau.fehler === null ? null
    : vorschau.fehler.felder.length > 0
      ? `${vorschau.fehler.text} (${vorschau.fehler.felder.map((f) => f.field).join(", ")})`
      : vorschau.fehler.text;
  const titel = `Layout „${stand.name}“ — ${stand.unternehmen.name}`;
  // Nach einer 409 (Fremder Stand) sind Felder UND Fuss gesperrt, bis „Neu laden“ den
  // aktuellen Stand geholt hat (Review-Fokus 2).
  const gesperrt = fremd !== null;
  const speichernAus = !ungespeichert || arbeitet || gesperrt;
  const verwerfenAus = !geaendert || arbeitet || gesperrt;
  // Erstfreigabe (Gesamtreview M1, Ruling 21.10.): Ein neues Layout trägt die Vorbelegung des Fußes und gilt als
  // „nicht geändert" (Review Focus 5) — freigeben lässt es sich trotzdem, solange es noch nie freigegeben wurde
  // und der Entwurf nicht leer ist. Ein leerer Bestandsentwurf hat weiterhin nichts freizugeben.
  const erstfreigabe = stand.freigegeben === null && Object.keys(stand.entwurf).length > 0;
  const freigebenAus = (!geaendert && !erstfreigabe) || arbeitet || gesperrt;

  /* Meldungen VOR allem anderen und AUSSERHALB der Kennwerte (Hausregel; Fixrunde 1, W4):
     Ein eingeklappter Steckbrief darf keinen Fehler verschlucken. Schlechtestes oben. */
  const meldungen: ReactNode[] = [];
  if (fremd !== null) {
    meldungen.push(
      <Hinweis key="fremd" art="fehler" className="lay__stoerung" aria-label="Fremder Stand"
        aktionen={(
          <LayKnopf deaktiviert={arbeitet}
            onClick={() => {
              if (ungespeichert) setDialog("neuladen");
              else void neuLaden(true);
            }}>Neu laden</LayKnopf>
        )}>
        <p>Das Layout wurde zwischenzeitlich geändert ({fremd.von},{" "}
          {DATUM.format(new Date(fremd.am))}). Speichern, Verwerfen und Freigeben sind
          gesperrt, bis der aktuelle Stand geladen ist.</p>
      </Hinweis>,
    );
  }
  if (stand.stoerung !== null) {
    meldungen.push(
      // Unerreichbare Ablage ist eine WARNUNG (Chip „warnung", K8 N2): Statuszeile in
      // Warnfarbe. Abweichende oder fehlende Ablage bleiben Alarm in Fehlerfarbe.
      <Hinweis key="stoerung" aria-label="Störung"
        art={stand.stoerung === "ABLAGE_NICHT_ERREICHBAR" ? "warnung" : "fehler"}
        className="lay__stoerung"
        aktionen={(
          <>
            {stand.stoerung === "ABLAGE_WEICHT_AB" && (
              <LayKnopf deaktiviert={arbeitet || gesperrt}
                onClick={() => { void ablageUebernehmen(); }}>Ablage übernehmen</LayKnopf>
            )}
            {stand.stoerung === "ABLAGE_NICHT_ERREICHBAR" ? (
              <LayKnopf deaktiviert={arbeitet || gesperrt}
                onClick={() => { void neuLaden(false); }}>Erneut prüfen</LayKnopf>
            ) : (
              <LayKnopf deaktiviert={arbeitet || gesperrt}
                onClick={() => { void freigabeOeffnen(); }}>Erneut freigeben</LayKnopf>
            )}
          </>
        )}>
        <p>{stand.stoerung === "ABLAGE_WEICHT_AB"
          ? "Ablage weicht ab: In der Render-Engine liegt ein anderer Stand als der "
            + "hier freigegebene — jemand hat die Ablage direkt beschrieben."
          : stand.stoerung === "ABLAGE_NICHT_ERREICHBAR"
            // Der Server unterscheidet „antwortet mit Fehler“ von „nicht erreichbar“
            // (Nachlese LE1); ohne Hinweis der feste Wortlaut.
            ? stand.ablageHinweis ?? "Engine-Ablage nicht erreichbar — Abgleich offen."
            : "Ablage fehlt: Die Render-Engine kennt dieses Layout nicht mehr — Belege "
              + "rendern mit dem Systemlayout."}</p>
      </Hinweis>,
    );
  }
  if (meldung !== null) {
    meldungen.push(
      <Hinweis key="meldung" art={meldung.art === "schlecht" ? "fehler" : "ok"}
        className="lay__meldung" aria-label="Meldung">{meldung.text}</Hinweis>,
    );
  }
  // Bei ABLAGE_NICHT_ERREICHBAR steht der Hinweis schon in der Störung.
  if (stand.ablageHinweis !== null && stand.stoerung !== "ABLAGE_NICHT_ERREICHBAR") {
    meldungen.push(
      <p key="hinweis" className="lay__hinweis" role="status">{stand.ablageHinweis}</p>,
    );
  }

  return (
    <Arbeitsdialog titel="Belegdesign" onAbbrechen={abbrechen} abbrechenGesperrt={arbeitet}
      steckbrief={(
        <Steckbrief art="dialog" titel={titel} kennung={stand.layoutRef}
          status={<Chip statusArt={chip.art}>{chip.text}</Chip>}
          aktionen={(
            <LayoutVarianten client={client} stand={stand}
              gesperrt={arbeitet || gesperrt || ungespeichert}
              onArbeitet={setArbeitet}
              onStand={(neu, text) => { uebernimm(neu, false); setMeldung({ text, art: "ok" }); }}
              onKopie={(neu) => {
                p.onZustand({ ungespeichert: false, listeNeu: true });
                p.onLayoutOeffnen?.(neu.id);
              }}
              onGeloescht={(name, ablageHinweis) => {
                p.onZustand({ ungespeichert: false, listeNeu: true });
                p.onGeloescht?.(name, ablageHinweis);
                p.onSchliessen();
              }}
              onFehler={fehlerZeigen} />
          )}
          werte={(
            <dl className="lay__kennwerte">
              <dt>Unternehmen</dt>
              <dd>{stand.unternehmen.name} ({stand.unternehmen.kuerzel}){" "}
                <Knopf art="leise" onClick={() => p.onUnternehmen(stand.companyId)}>
                  Unternehmen öffnen</Knopf></dd>
              <dt>Freigabe</dt>
              <dd>{stand.freigegeben === null ? "noch nicht freigegeben"
                : `freigegeben am ${DATUM.format(new Date(stand.freigegeben.am))}`
                  + (stand.freigegeben.von === null ? "" : ` von ${stand.freigegeben.von}`)}</dd>
              <dt>Verwendung</dt>
              <dd>{verwendungText(stand)}</dd>
            </dl>
          )} />
      )}
      meldungen={meldungen.length > 0 ? meldungen : undefined}
      fussLinks={(
        <>
          {/* Sichtbarer Ausgang im Fuss (Dirk 11.10.2026), seit Entscheid 36 zusätzlich das Kreuz:
              „Verwerfen“ verwirft nur den gespeicherten Entwurf — „Schließen“ geht denselben Weg wie
              Kreuz und Escape (Verwerfen-Rückfrage bei ungespeicherten Eingaben, dann zur Layoutliste). */}
          <Knopf art="sekundaer" deaktiviert={arbeitet}
            onClick={() => { if (!arbeitet) void abbrechen(); }}>Schließen</Knopf>
          <Knopf art="sekundaer" deaktiviert={verwerfenAus}
            onClick={() => { if (!verwerfenAus) setDialog("verwerfen"); }}>Verwerfen</Knopf>
        </>
      )}
      hinweis={ungespeichert ? "Ungespeicherte Änderungen"
        : geaendert ? "Entwurf gespeichert, nicht freigegeben"
          : erstfreigabe ? "Vorbelegt, noch nicht freigegeben" : "Keine Änderungen"}
      fuss={(
        <>
          <Knopf art="primaer" deaktiviert={speichernAus}
            onClick={() => { if (!speichernAus) void speichern(); }}>Speichern</Knopf>
          <Knopf art="sekundaer" deaktiviert={freigebenAus}
            onClick={() => { if (!freigebenAus) void freigabeOeffnen(); }}>Freigeben</Knopf>
        </>
      )}>
      <div className="lay">
        <div className="lay__arbeit" data-designer="rechts">
          <div className="lay__blattspalte">
            <div className="lay__werkzeuge">
              <Auswahl beschriftung="Belegart" kompakt className="lay__belegart" wert={belegart}
                onGeaendert={(d) => {
                  const art = d.wert as Belegart;
                  setBelegart(art);
                  vorschau.sofort(auftrag(entwurf, art));
                }}>
                {k.belegarten.map((b) => <option key={b.art} value={b.art}>{b.label}</option>)}
              </Auswahl>
              {/* Muster/Echter Beleg und Breite/100 % sind Umschalter: `Optionen` als Segmente
                  (me-design 284f4ed, Entscheid 48). Ist ein echter Beleg gewählt, zeigt der
                  Knopf daneben seinen Titel und öffnet die Auswahl erneut (ein Klick auf das
                  schon gewählte Segment meldet keine Änderung). */}
              <Optionen beschriftung="Beleg" darstellung="segmente" richtung="waagerecht"
                wert={beleg.art === "ECHT" ? "ECHT" : "MUSTER"}
                onGeaendert={(d) => {
                  if (d.wert === "MUSTER") belegWaehlen({ art: "MUSTER" });
                  else if (d.wert === "ECHT") void echteLaden();
                }}>
                <option value="MUSTER">Muster</option>
                <option value="ECHT">Echter Beleg</option>
              </Optionen>
              {beleg.art === "ECHT" && (
                <LayKnopf onClick={() => { void echteLaden(); }}>{beleg.titel} ändern …</LayKnopf>
              )}
              <Optionen beschriftung="Zoom" darstellung="segmente" richtung="waagerecht"
                wert={zoom}
                onGeaendert={(d) => { if (d.wert === "breite" || d.wert === "voll") setZoom(d.wert); }}>
                <option value="breite">Breite</option>
                <option value="voll">100 %</option>
              </Optionen>
              <Schalter beschriftung="Hilfslinien" an={hilfslinien}
                onGeaendert={(d) => setHilfslinien(d.an)} />
              <Schalter beschriftung="Spaltenlinien" an={spaltenlinien}
                onGeaendert={(d) => setSpaltenlinien(d.an)} />
              <LayKnopf deaktiviert={vorschau.laedt}
                onClick={() => vorschau.sofort(auftrag())}>Aktualisieren</LayKnopf>
              <span className="lay__zaehler">
                {vorschau.stand === null || vorschau.stand.anzahl === null ? ""
                  : vorschau.stand.anzahl === 1 ? "1 Seite" : `${vorschau.stand.anzahl} Seiten`}
              </span>
            </div>
            {(vorschau.stand?.hinweise ?? []).length > 0 && (
              <ul className="lay__vorschauhinweise">
                {vorschau.stand!.hinweise.map((h) => <li key={h}>{h}</li>)}
              </ul>
            )}
            <LayoutBlatt vorschau={vorschau.stand} laedt={vorschau.laedt} fehler={blattFehler}
              zoom={zoom} hilfslinien={hilfslinien} raender={raender}
              kopfbereich={faehig && (hilfslinien || register === "marke") ? {
                erste: kopfbereichMm(entwurf, "ersteSeiteMm"),
                folge: kopfbereichMm(entwurf, "folgeseitenMm") } : null}
              fuss={faehig && (hilfslinien || register === "marke")
                ? fussLinien(entwurf, raender) : null}
              spaltenlinien={spaltenlinien
                ? spaltenLinien(spaltenAus(entwurf, k.spalten)) : null}
              objekte={blattObjekte}
              gewaehlt={register === "marke" ? auswahl : null}
              gesperrt={arbeitet || fremd !== null}
              onGewaehlt={(id) => {
                // Auswahl ↔ Register Marke: wer im Blatt wählt, sieht das Objekt dort.
                setAuswahl(id);
                if (id !== null) setRegister("marke");
              }}
              onZieht={blattZieht}
              onVerschoben={blattVerschoben} />
          </div>
          <div className="lay__gestaltung">
            {/* Die Reiter stehen DIREKT über ihrem Panel (Hausregel „Reiter nach unten
                offen“: aktiver Reiter und Panel sind EINE Fläche) — nicht im Slot `register`
                des Arbeitsdialogs, der über Blatt UND Gestaltung reichte (Review A2 W1; bis
                me-design Befund 30 beantwortet). Im Einspalter unter 900 px stehen sie so
                über dem Panel unter dem Blatt. */}
            <LayoutReiterleiste ref={reiterleiste} eintraege={eintraege} aktiv={register}
              onAktiv={setRegister} fehler={fehlerZahl} />
            {/* `fieldset disabled` sperrt im Zustand „Fremder Stand“ jedes Feld des Panels
                auf einmal (Review-Fokus 2) — ohne den Baum umzubauen. Die ME-Felder sperren
                sich darüber selbst (formDisabledCallback: gesperrt, der Fokus bleibt; me-design
                ba10760), seit 6bfec97 auch die Knöpfe (Befund P4b-1). `PanelSperre` blendet
                nur noch „Systemwert“ aus. */}
            <fieldset className="lay__sperre" disabled={gesperrt}>
              <PanelSperre.Provider value={gesperrt}>
                <LayoutRegisterPanel eintraege={eintraege} aktiv={register}
                  stand={katalogMitFaehigkeiten(stand, (f) => (f === "ansprechpartnerBlock" && apFaehig)
                    || (f === "positionsUeberschriften" && ueberschriftenFaehig))}
                  entwurf={entwurfAnzeige} feldFehler={feldFehler} belegart={belegart}
                  onEntwurf={eingabe}
                  onFeldVerlassen={() => vorschau.anfordern(auftrag(entwurfRef.current))}
                  nummerierungen={nummerierungen}
                  onNummerierung={nummerierungWaehlen} />
              </PanelSperre.Provider>
            </fieldset>
          </div>
        </div>

        {dialog === "neuladen" && (
          // Gefahr-Frage nur, wenn wirklich Eingaben verloren gehen (Rot-Regel: `useBestaetigung`); „Layout
          // freigeben“ ist keine (nichts geht verloren), „Entwurf verwerfen“ und „Grafik
          // entfernen“ sind es immer.
          <Modal titel="Layout neu laden" frage gefahr={ungespeichert}
            onAbbrechen={() => setDialog(null)} abbrechenGesperrt={arbeitet}>
            <p>Der aktuelle Stand wird geladen. Ungespeicherte Eingaben gehen dabei verloren.</p>
            <ModalFuss speichernText="Neu laden" gefahr={ungespeichert} speichernGesperrt={arbeitet}
              abbrechenGesperrt={arbeitet} onSpeichern={() => { void neuLaden(true); }}
              onAbbrechen={() => setDialog(null)} />
          </Modal>
        )}

        {dialog === "freigabe" && (
          <Modal titel="Layout freigeben" frage onAbbrechen={() => setDialog(null)}
            abbrechenGesperrt={arbeitet}>
            {/* Meldung vor der Liste; der Riegel steht auch in freigeben(). */}
            {ungespeichert && (
              <Hinweis art="fehler">Zuerst speichern — ungespeicherte Eingaben würden nicht
                freigegeben.</Hinweis>
            )}
            <p>{stand.vorgabe
              ? `Ab der Freigabe rendert jeder Beleg von ${stand.unternehmen.name} ohne eigene Layoutwahl `
                + "mit diesem Layout"
              : `Ab der Freigabe rendert jeder Beleg mit dem Layout „${stand.name}“ damit`}
              {" "}— Vorschau, PDF, Ablage und E-Rechnung.</p>
            {stand.aenderungen.length > 0 ? (
              <>
                <p>Geändert gegenüber der letzten Freigabe:</p>
                <ul className="lay__aenderungen">
                  {stand.aenderungen.map((a) => <li key={a}>{a}</li>)}
                </ul>
              </>
            ) : <p>Gegenüber der letzten Freigabe ist nichts geändert.</p>}
            <ModalFuss speichernText="Freigeben"
              speichernGesperrt={arbeitet || ungespeichert || gesperrt}
              abbrechenGesperrt={arbeitet} onSpeichern={() => { void freigeben(); }}
              onAbbrechen={() => setDialog(null)} />
          </Modal>
        )}

        {dialog === "verwerfen" && (
          <Modal titel="Entwurf verwerfen" frage gefahr onAbbrechen={() => setDialog(null)}
            abbrechenGesperrt={arbeitet}>
            <p>{stand.freigegeben === null
              ? "Der Entwurf und alle Grafiken werden entfernt — es gilt wieder das Systemlayout."
              : "Entwurf und Grafiken werden auf den freigegebenen Stand zurückgesetzt."}
              {ungespeichert ? " Ungespeicherte Eingaben gehen verloren." : ""}</p>
            <ModalFuss speichernText="Verwerfen" gefahr speichernGesperrt={arbeitet}
              abbrechenGesperrt={arbeitet} onSpeichern={() => { void verwerfen(); }}
              onAbbrechen={() => setDialog(null)} />
          </Modal>
        )}

        {dialog === "echt" && (
          <Modal titel="Echten Beleg wählen" onAbbrechen={() => setDialog(null)} breite="mittel">
            {echte === null ? <p className="lay__hinweis">Lädt …</p>
              : echte.length === 0 ? <p className="lay__hinweis">Dieses Unternehmen hat noch keine Belege.</p>
              : (
                <ul className="lay__belegliste">
                  {echte.map((b) => {
                    const text = `${k.belegarten.find((a) => a.art === b.belegart)?.label ?? b.belegart} `
                      + `${b.nummer ?? "Entwurf"} · ${b.datum ?? "—"} · ${b.kunde}`;
                    return (
                      <li key={`${b.quelle}-${b.id}`}>
                        <LayKnopf art="leise" className="lay__belegzeile"
                          onClick={() => {
                            setBelegart(b.belegart);
                            belegWaehlen({ art: "ECHT", quelle: b.quelle, id: b.id, titel: text });
                          }}>{text}</LayKnopf>
                      </li>
                    );
                  })}
                </ul>
              )}
            <ModalFuss speichernText="Muster verwenden" onSpeichern={() => belegWaehlen({ art: "MUSTER" })}
              onAbbrechen={() => setDialog(null)} />
          </Modal>
        )}
      </div>
    </Arbeitsdialog>
  );
}
